import { Children, cloneElement, isValidElement, type Key, type ReactNode } from "react";
import {
  Tab as RACTab,
  TabList as RACTabList,
  TabPanel as RACTabPanel,
  Tabs as RACTabs,
  type TabListProps as RACTabListProps,
  type TabPanelProps as RACTabPanelProps,
  type TabProps as RACTabProps,
  type TabsProps as RACTabsProps
} from "react-aria-components";

import { cn } from "@design-lab/shared/lib/tailwindcss";

import { composeTailwindRenderProps } from "./design-system/utils";

type TabsProps = Omit<RACTabsProps, "selectedKey" | "onSelectionChange"> & {
  value?: string;
  onValueChange?: (value: string) => void;
};

function Tabs(props: TabsProps) {
  const { value, onValueChange, className = "", ...rest } = props;

  const handleSelectionChange = onValueChange
    ? (key: Key) => onValueChange(String(key))
    : undefined;

  return (
    <RACTabs
      {...rest}
      selectedKey={value ?? null}
      {...(handleSelectionChange && { onSelectionChange: handleSelectionChange })}
      className={className}
    />
  );
}

type TabsListProps<T extends object> = RACTabListProps<T>;

function TabsList<T extends object>(props: TabsListProps<T>) {
  return (
    <RACTabList
      {...props}
      data-slot="tabs-list"
      className={composeTailwindRenderProps(
        props.className,
        "bg-muted text-muted-foreground flex h-9 w-full items-center gap-1 rounded-lg p-1"
      )}
    />
  );
}

type TabsTriggerProps = Omit<RACTabProps, "id"> & {
  value: string;
  asChild?: boolean;
  disabled?: boolean;
  children?: ReactNode;
};

function TabsTrigger(props: TabsTriggerProps) {
  const { value, asChild, disabled, children, className, ...rest } = props;

  if (asChild) {
    return (
      <RACTab
        {...rest}
        id={value}
        isDisabled={disabled ?? false}
        className={composeTailwindRenderProps(
          className,
          "group w-full cursor-pointer outline-none"
        )}
      >
        {({ isSelected }) => {
          const child = Children.only(children);
          if (isValidElement<Record<string, unknown>>(child)) {
            return cloneElement(child, { "data-selected": isSelected || undefined });
          }
          return children;
        }}
      </RACTab>
    );
  }

  return (
    <RACTab
      {...rest}
      id={value}
      isDisabled={disabled ?? false}
      data-slot="tabs-trigger"
      className={composeTailwindRenderProps(
        className,
        cn(
          "ring-offset-background inline-flex flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium outline-none transition-all",
          "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2",
          "disabled:pointer-events-none disabled:opacity-50",
          "data-[selected]:bg-background data-[selected]:text-foreground data-[selected]:shadow"
        )
      )}
    >
      {children}
    </RACTab>
  );
}

type TabsContentProps = Omit<RACTabPanelProps, "id"> & {
  value: string;
};

function TabsContent(props: TabsContentProps) {
  const { value, className, ...rest } = props;

  return (
    <RACTabPanel
      {...rest}
      id={value}
      data-slot="tabs-content"
      data-testid={`tab-${value}-panel`}
      className={composeTailwindRenderProps(
        className,
        "ring-offset-background focus-visible:ring-ring mt-2 outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      )}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
